'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, ExternalLink, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InitiativeLookup } from '@/components/lookups/InitiativeLookup';
import { Lookup, type LookupOption } from '@/components/ui/lookup';
import type { ApiAccessGrant, ApiInitiative } from '@/lib/api/types';
import { buildAccessGrantUrl, type AccessGrantTarget } from '@/lib/access-grants/build-url';
import { useAccessGrants } from '@/lib/hooks/useAccessGrants';
interface CommonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional override for the dialog title. */
  title?: string;
  /** Optional override for the dialog description. */
  description?: string;
}

/**
 * Mode A — fixed grant, the user picks which initiative under that grant.
 * Use this from the access grants admin table on entity-scoped grant rows.
 */
interface PickTargetProps extends CommonProps {
  mode: 'pick-target';
  /** The grant whose token will be reused. */
  grant: ApiAccessGrant;
  /**
   * Optional restriction. If provided, the picker only searches initiatives
   * owned by this entity. Defaults to the grant's `objectId` when the grant
   * is entity-scoped.
   */
  ownerEntityId?: string | number;
  /** Used to filter when the grant is initiative-scoped (less common). */
  initiative?: never;
  grantPickerEntityScopeId?: never;
}

/**
 * Mode B — fixed target initiative, the user picks an existing grant that
 * applies to it (either an entity-level grant on the owning entity or an
 * initiative-level grant on this initiative). Use this from InitiativeDetail.
 * When `initiative` is omitted, the generated URL uses `customSharePath` or
 * falls back to the entity path.
 */
interface PickGrantProps extends CommonProps {
  mode: 'pick-grant';
  /** The initiative the user wants to share. Optional — when omitted, uses customSharePath or entity path. */
  initiative?: Pick<ApiInitiative, 'id' | 'name' | 'ownerType' | 'ownerId'>;
  /**
   * Required. The entity that owns the initiative — used to look up entity-
   * level grants alongside initiative-level ones.
   */
  grantPickerEntityScopeId: string | number;
  /**
   * Optional custom path to use in the generated URL instead of the initiative path.
   * Useful when sharing a page like /entities/:id/initiatives rather than a specific initiative.
   */
  customSharePath?: string;
  grant?: never;
  ownerEntityId?: never;
}
export type AccessGrantLinkDialogProps = PickTargetProps | PickGrantProps;

/**
 * Reusable dialog for composing a tokenised access link.
 *
 * - `mode="pick-target"` keeps the grant fixed; user chooses which initiative
 *   to deep-link into. Useful from a list of grants.
 * - `mode="pick-grant"` keeps the initiative fixed; user picks an existing
 *   grant. Useful from an initiative detail page where the admin wants to
 *   "share" the initiative via one of their existing grants.
 *
 * No new grant is created in either mode — only an existing token is reused.
 */
export function AccessGrantLinkDialog(props: AccessGrantLinkDialogProps) {
  const {
    open,
    onOpenChange,
    title,
    description
  } = props;
  const [initiative, setInitiative] = useState<LookupOption<ApiInitiative> | null>(null);
  const [grant, setGrant] = useState<LookupOption<ApiAccessGrant> | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset on open / when the fixed side changes
  useEffect(() => {
    if (open) {
      setCopied(false);
      if (props.mode === 'pick-target') {
        setInitiative(null);
      } else {
        setGrant(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, props.mode === 'pick-target' ? props.grant?.id : props.initiative?.id]);

  // Resolve the active grant + target from the props + selection
  const activeGrant: ApiAccessGrant | null = props.mode === 'pick-target' ? props.grant : grant?.data ?? null;
  const activeTarget: AccessGrantTarget | null = useMemo(() => {
    if (props.mode === 'pick-target') {
      if (!initiative) return null;
      return {
        objectType: 'initiative',
        objectId: initiative.id
      };
    }
    if (props.initiative) {
      return {
        objectType: 'initiative',
        objectId: props.initiative.id
      };
    }
    // No specific initiative — target is the entity itself
    return {
      objectType: 'entity',
      objectId: Number(props.grantPickerEntityScopeId)
    };
  }, [props, initiative]);
  const generatedUrl = useMemo(() => {
    if (!activeGrant) return '';
    // When a customSharePath is provided (e.g. /entities/123/initiatives), use it directly
    if (props.mode === 'pick-grant' && props.customSharePath && !props.initiative) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${props.customSharePath}?access=${activeGrant.token}`;
    }
    if (!activeTarget) return '';
    return buildAccessGrantUrl({
      token: activeGrant.token,
      target: activeTarget
    });
  }, [activeGrant, activeTarget, props]);
  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(generatedUrl);
        setCopied(true);
        toast.success('Link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      toast.error('Unable to copy. Select the link and copy manually.');
    }
  };
  const handleOpenLink = () => {
    if (!generatedUrl) return;
    if (typeof window !== 'undefined') {
      window.open(generatedUrl, '_blank');
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title ?? defaultTitleFor(props)}</DialogTitle>
          <DialogDescription>{description ?? defaultDescriptionFor(props)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {props.mode === 'pick-target' ? <PickInitiativeRow value={initiative} onChange={setInitiative} ownerEntityId={props.ownerEntityId ?? (props.grant.objectType === 'entity' ? props.grant.objectId : undefined)} /> : <PickGrantRow value={grant} onChange={setGrant} entityScopeId={props.grantPickerEntityScopeId} initiativeId={props.initiative?.id} />}

          {generatedUrl && <div>
              <Label className="text-xs uppercase tracking-wide text-gray-500">
                Shareable link
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={generatedUrl} readOnly onClick={e => e.currentTarget.select()} />
                <Button type="button" onClick={handleCopy} className="shrink-0">
                  {copied ? <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied
                    </> : <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </>}
                </Button>
              </div>
              {activeGrant && <p className="text-xs text-gray-500 mt-1">
                  Uses the <strong>{activeGrant.label || 'untitled grant'}</strong> token. Anyone
                  with this link can view the initiative until the grant expires, is exhausted, or
                  is revoked.
                </p>}
            </div>}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {generatedUrl && <Button type="button" variant="outline" onClick={handleOpenLink}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Open
            </Button>}
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}
function defaultTitleFor(props: AccessGrantLinkDialogProps): string {
  if (props.mode === 'pick-target') return 'Generate initiative link';
  return 'Share via access grant';
}
function defaultDescriptionFor(props: AccessGrantLinkDialogProps): string {
  if (props.mode === 'pick-target') {
    return `Pick an initiative under ${props.grant.label ? `“${props.grant.label}”` : 'this grant'} to build a URL that lands directly on it. The same token is reused — no new grant is created.`;
  }
  return 'Pick an existing access grant to build a shareable link for this initiative. No new grant is created.';
}
interface PickInitiativeRowProps {
  value: LookupOption<ApiInitiative> | null;
  onChange: (value: LookupOption<ApiInitiative> | null) => void;
  ownerEntityId?: string | number;
}
function PickInitiativeRow(t0) {
  const $ = _c(7);
  const {
    value,
    onChange,
    ownerEntityId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Label htmlFor="access-grant-link-initiative">Initiative</Label>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = ownerEntityId ? "entity" : undefined;
  const t3 = ownerEntityId ? "Search this entity's initiatives..." : "Search initiatives...";
  let t4;
  if ($[1] !== onChange || $[2] !== ownerEntityId || $[3] !== t2 || $[4] !== t3 || $[5] !== value) {
    t4 = <div>{t1}<InitiativeLookup value={value} onChange={onChange} ownerId={ownerEntityId} ownerType={t2} placeholder={t3} /></div>;
    $[1] = onChange;
    $[2] = ownerEntityId;
    $[3] = t2;
    $[4] = t3;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
interface PickGrantRowProps {
  value: LookupOption<ApiAccessGrant> | null;
  onChange: (value: LookupOption<ApiAccessGrant> | null) => void;
  entityScopeId: string | number;
  initiativeId?: number | string;
}

/**
 * Internal grant picker — fetches all entity-scoped grants (which includes
 * any initiative-scoped grants for this entity's initiatives), then narrows
 * to grants that actually apply to `initiativeId` (or all entity-level grants
 * when no initiativeId is provided).
 */
function PickGrantRow(t0) {
  const $ = _c(29);
  const {
    value,
    onChange,
    entityScopeId,
    initiativeId
  } = t0;
  const [searchQuery, setSearchQuery] = useState("");
  const [enabled, setEnabled] = useState(false);
  const t1 = searchQuery || undefined;
  let t2;
  if ($[0] !== entityScopeId || $[1] !== t1) {
    t2 = {
      entityScopeId,
      search: t1,
      limit: 50
    };
    $[0] = entityScopeId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data,
    isLoading
  } = useAccessGrants(t2);
  let applicable;
  let t3;
  if ($[3] !== data?.grants || $[4] !== entityScopeId || $[5] !== initiativeId) {
    const allGrants = data?.grants ?? [];
    let t4;
    if ($[8] !== entityScopeId || $[9] !== initiativeId) {
      t4 = g => {
        if (g.revokedAt) {
          return false;
        }
        if (g.expiresAt && new Date(g.expiresAt).getTime() <= Date.now()) {
          return false;
        }
        if (g.maxUses != null && g.useCount >= g.maxUses) {
          return false;
        }
        if (g.objectType === "entity" && String(g.objectId) === String(entityScopeId)) {
          return true;
        }
        if (initiativeId && g.objectType === "initiative" && String(g.objectId) === String(initiativeId)) {
          return true;
        }
        return false;
      };
      $[8] = entityScopeId;
      $[9] = initiativeId;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    applicable = allGrants.filter(t4);
    t3 = applicable.map(_temp);
    $[3] = data?.grants;
    $[4] = entityScopeId;
    $[5] = initiativeId;
    $[6] = applicable;
    $[7] = t3;
  } else {
    applicable = $[6];
    t3 = $[7];
  }
  const options = t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Label htmlFor="access-grant-link-grant">Access grant</Label>;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setEnabled(true);
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== enabled || $[14] !== options) {
    t6 = enabled ? options : [];
    $[13] = enabled;
    $[14] = options;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = enabled && isLoading;
  const t8 = initiativeId ? "No active grants apply to this initiative" : "No active entity grants found";
  let t9;
  if ($[16] !== onChange || $[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== value) {
    t9 = <Lookup value={value} onChange={onChange} onSearch={setSearchQuery} onOpen={t5} options={t6} isLoading={t7} placeholder="Search active grants..." emptyMessage={t8} />;
    $[16] = onChange;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = value;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== applicable.length || $[23] !== enabled || $[24] !== isLoading) {
    t10 = applicable.length === 0 && !isLoading && enabled && <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><KeyRound className="h-3 w-3" />You may need to create a grant first under Entity Admin → Access Grants.</p>;
    $[22] = applicable.length;
    $[23] = enabled;
    $[24] = isLoading;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <div>{t4}{t9}{t10}</div>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}
function _temp(g_0) {
  return {
    id: g_0.id,
    label: `${g_0.label || "Untitled grant"} — ${g_0.objectType === "entity" ? "whole entity" : `initiative #${g_0.objectId}`}`,
    data: g_0
  };
}